import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  relatedUsersLinks,
  userMfaDisplay,
  userRoleDisplay,
  userStatusDisplay,
  type UserListRow,
  type UserSortKey,
} from "../lib/users-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: UserSortKey;
  onSortChange: (value: UserSortKey) => void;
  organizationLabel: string;
};

export function UsersFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  organizationLabel,
}: FilterBarProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "active", label: "فعال" },
    { id: "inactive", label: "غیرفعال" },
    { id: "owner", label: "مالک" },
    { id: "publisher", label: "ناشر" },
    { id: "editor", label: "ویرایشگر" },
    { id: "auditor", label: "ممیز" },
    { id: "mfa_on", label: "MFA فعال" },
    { id: "mfa_off", label: "MFA خاموش" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر کاربران"
    >
      <p className="sites-inventory__muted" style={{ width: "100%" }}>
        دامنه سازمان فعال:{" "}
        <strong>
          <LtrIsolate mono>{organizationLabel}</LtrIsolate>
        </strong>
      </p>
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر کاربران"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی کاربر</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="نام، ایمیل، نقش…"
          aria-label="جستجوی کاربران"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as UserSortKey)}
          aria-label="مرتب‌سازی فهرست کاربران"
        >
          <option value="name">نام</option>
          <option value="email">ایمیل</option>
          <option value="role">نقش</option>
          <option value="status">وضعیت</option>
          <option value="login">آخرین ورود</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function UserInspectSummary({
  item,
  organizationLabel,
  isSelf,
  actions,
}: {
  item: UserListRow;
  organizationLabel: string;
  isSelf: boolean;
  actions?: ReactNode;
}) {
  const role = userRoleDisplay(item.role);
  const status = userStatusDisplay(item.active);
  const mfa = userMfaDisplay(item.mfaEnabled);
  const links = relatedUsersLinks();

  return (
    <section className="content-inspect" aria-labelledby="user-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">کاربران و نقش‌ها</p>
          <h2 id="user-inspect-title" className="content-inspect__name">
            {item.displayName}
            {isSelf ? " (شما)" : ""}
          </h2>
          <p className="content-inspect__id">
            شناسه <LtrIsolate mono>{item.id}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="نقش و وضعیت">
          <span className={`state-badge ${TONE_BADGE[role.tone]}`}>{role.label}</span>
          <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
          <span className={`state-badge ${TONE_BADGE[mfa.tone]}`}>{mfa.label}</span>
        </div>
      </header>

      {role.detail && <p className="content-inspect__eligibility-detail">{role.detail}</p>}
      {mfa.detail && <p className="content-inspect__eligibility-detail">{mfa.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>ایمیل</dt>
          <dd>
            <LtrIsolate mono>{item.email}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>نقش فنی</dt>
          <dd>
            <LtrIsolate mono>{item.role}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>دامنه سازمان فعال</dt>
          <dd>
            <LtrIsolate mono>{organizationLabel}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>آخرین ورود</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(item.lastLoginAt)}</LtrIsolate>
          </dd>
        </div>
      </dl>

      <nav className="content-inspect__links" aria-label="مسیرهای مرتبط دسترسی">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

export function UsersInventoryList({
  rows,
  selectedId,
  currentUserId,
  onSelect,
  renderActions,
}: {
  rows: UserListRow[];
  selectedId: string | null;
  currentUserId: string | undefined;
  onSelect: (id: string) => void;
  renderActions: (row: UserListRow) => ReactNode;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول کاربران"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">نام</th>
              <th scope="col">ایمیل</th>
              <th scope="col">نقش</th>
              <th scope="col">MFA</th>
              <th scope="col">وضعیت</th>
              <th scope="col">آخرین ورود</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const role = userRoleDisplay(row.role);
              const status = userStatusDisplay(row.active);
              const mfa = userMfaDisplay(row.mfaEnabled);
              const isSelf = row.id === currentUserId;
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      {row.displayName}
                      {isSelf ? " (شما)" : ""}
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.email}>
                      <LtrIsolate mono>{row.email}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[role.tone]}`}>{role.label}</span>
                    <div
                      className="sites-inventory__muted inventory-cell-truncate"
                      title={row.role}
                    >
                      <LtrIsolate mono>{row.role}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[mfa.tone]}`}>{mfa.label}</span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.lastLoginAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.lastLoginAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های کاربران"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const role = userRoleDisplay(row.role);
          const status = userStatusDisplay(row.active);
          const mfa = userMfaDisplay(row.mfaEnabled);
          const isSelf = row.id === currentUserId;
          return (
            <li
              key={row.id}
              className={`sites-inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  {row.displayName}
                  {isSelf ? " (شما)" : ""}
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[role.tone]}`}>{role.label}</span>
                  <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  <span className={`state-badge ${TONE_BADGE[mfa.tone]}`}>{mfa.label}</span>
                </span>
                <span className="sites-inventory__muted">
                  <LtrIsolate mono>{row.email}</LtrIsolate>
                </span>
                <span className="sites-inventory__muted">
                  نقش فنی: <LtrIsolate mono>{row.role}</LtrIsolate>
                  {" · ورود: "}
                  <LtrIsolate mono>{formatKabulDisplay(row.lastLoginAt)}</LtrIsolate>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
